import type { ClientReportListItemDTO, ClientReportDetailDTO, Language } from '@brainkey/shared';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { clientReportsApi } from '../api/endpoints';
import { queryKeys } from '../queryClient';

const IN_PROGRESS_STATUSES = new Set(['PENDING', 'GENERATING']);

/** Picks "the" report to show a client who may now have more than one
 * language variant (report-language regeneration). `reports` is
 * already sorted newest-first by the API — prefer the one matching the
 * client's CURRENT `languagePref` (regardless of when it was created) over
 * plain recency, so switching back to a language you already generated
 * re-surfaces that report instead of silently doing nothing (an
 * older-but-still-current-language report would otherwise be permanently
 * shadowed by whichever language was generated most recently). Falls back
 * to the newest report overall when none match (no language chosen yet, or
 * a fresh trigger still PENDING in the target language). */
export function selectCurrentReport(
  reports: ClientReportListItemDTO[] | undefined,
  languagePref: Language | undefined,
): ClientReportListItemDTO | null {
  if (!reports || reports.length === 0) return null;
  const inPreferredLanguage = languagePref
    ? reports.find((r) => r.language === languagePref)
    : undefined;
  return inPreferredLanguage ?? reports[0] ?? null;
}

/** Polls every 2s while any report is PENDING/GENERATING, else stops —
 * mirrors the coach portal's useReport cadence exactly (CP-P2-T03). */
export function useClientReports(): UseQueryResult<ClientReportListItemDTO[]> {
  return useQuery({
    queryKey: queryKeys.clientReports.list,
    queryFn: async ({ signal }) => (await clientReportsApi.list(signal)).reports,
    refetchInterval: (query) => {
      const hasInProgress =
        query.state.data?.some((r) => IN_PROGRESS_STATUSES.has(r.status)) ?? false;
      return hasInProgress ? 2000 : false;
    },
  });
}

/** Fetched once a report is GENERATED/DELIVERED — not part of the polling
 * loop itself (the list above is what's polled). */
export function useClientReport(id: string | null): UseQueryResult<ClientReportDetailDTO> {
  return useQuery({
    queryKey: queryKeys.clientReports.detail(id ?? ''),
    queryFn: ({ signal }) => clientReportsApi.getById(id ?? '', signal).then((res) => res.report),
    enabled: id !== null && id !== '',
  });
}
